import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Check, Search, Download } from 'lucide-react';
import api from '../../../api';
import { format } from 'date-fns';

export default function AdminContacts() {
    const [contacts, setContacts] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, new, read, responded
    const [searchTerm, setSearchTerm] = useState('');

    const loadContacts = useCallback(async () => {
        try {
            setLoading(true);
            const statusFilter = filter === 'all' ? null : filter;
            const [contactsData, statsData] = await Promise.all([
                api.getContacts(statusFilter),
                api.getContactStats()
            ]);
            setContacts(contactsData);
            setStats(statsData);
        } catch (error) {
            console.error('Error loading contacts:', error);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadContacts();
    }, [filter]);

    const handleStatusChange = async (contactId, newStatus) => {
        try {
            await api.updateContactStatus(contactId, newStatus);
            loadContacts();
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        }
    };

    const handleExport = async () => {
        try {
            const blob = await api.exportContacts();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Failed to export contacts');
        }
    };

    const filteredContacts = contacts.filter(contact =>
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getInquiryColor = (type) => {
        const colors = {
            general: '#06b6d4',
            partnership: '#f43f5e',
            event: '#a855f7',
            community: '#f97316'
        };
        return colors[type] || '#06b6d4';
    };

    const getStatusBadge = (status) => {
        const styles = {
            new: { bg: '#ef444420', color: '#ef4444', label: 'New' },
            read: { bg: '#06b6d420', color: '#06b6d4', label: 'Read' },
            responded: { bg: '#10b98120', color: '#10b981', label: 'Resolved' },
            archived: { bg: '#64748b20', color: '#64748b', label: 'Archived' }
        };
        const style = styles[status] || styles.new;
        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                background: style.bg,
                color: style.color
            }}>
                {style.label}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
                        Contact Submissions
                    </h2>
                    <p style={{ color: 'var(--text-muted)' }}>
                        Manage and respond to contact form submissions
                    </p>
                </div>
                <button
                    onClick={handleExport}
                    className="btn-premium btn-premium-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <Download size={18} />
                    Export to Excel
                </button>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Total Submissions
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary)' }}>
                            {stats.total}
                        </div>
                    </div>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            New
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--error)' }}>
                            {stats.unread || stats.new || 0}
                        </div>
                    </div>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Read
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--secondary)' }}>
                            {stats.read}
                        </div>
                    </div>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Resolved
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--success)' }}>
                            {stats.responded || stats.resolved || 0}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '24px',
                flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {['all', 'new', 'read', 'responded'].map(status => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`btn ${filter === status ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ textTransform: 'capitalize', padding: '8px 16px', fontSize: '14px' }}
                        >
                            {status === 'responded' ? 'Resolved' : status}
                        </button>
                    ))}
                </div>
                <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
                    <Search size={18} style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)'
                    }} />
                    <input
                        type="text"
                        placeholder="Search contacts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-input"
                        style={{ paddingLeft: '40px' }}
                    />
                </div>
            </div>

            {/* Contacts List */}
            {loading ? (
                <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-muted)' }}>Loading contacts...</p>
                </div>
            ) : filteredContacts.length === 0 ? (
                <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                    <Mail size={48} style={{ color: 'var(--text-muted)', margin: '0 auto 16px' }} />
                    <p style={{ color: 'var(--text-muted)' }}>No contact submissions found</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {filteredContacts.map(contact => (
                        <div key={contact.id} className="admin-card-premium" style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                        <h3 style={{ fontSize: '18px', fontWeight: '600' }}>
                                            {contact.name}
                                        </h3>
                                        {getStatusBadge(contact.status)}
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            background: `${getInquiryColor(contact.inquiry_type)}20`,
                                            color: getInquiryColor(contact.inquiry_type),
                                            textTransform: 'capitalize'
                                        }}>
                                            {contact.inquiry_type}
                                        </span>
                                    </div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>
                                        {contact.email}
                                        {contact.organization && ` • ${contact.organization}`}
                                    </div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                                        {format(new Date(contact.created_at), 'MMM d, yyyy • h:mm a')}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {contact.status === 'new' && (
                                        <button
                                            onClick={() => handleStatusChange(contact.id, 'read')}
                                            className="btn-premium btn-premium-secondary"
                                            style={{ padding: '8px 12px', fontSize: '13px' }}
                                            title="Mark as read"
                                        >
                                            <Check size={16} />
                                        </button>
                                    )}
                                    {contact.status !== 'responded' && (
                                        <button
                                            onClick={() => handleStatusChange(contact.id, 'responded')}
                                            className="btn-premium btn-premium-primary"
                                            style={{ padding: '8px 12px', fontSize: '13px' }}
                                            title="Mark as resolved"
                                        >
                                            Resolve
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div style={{
                                padding: '16px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '8px',
                                borderLeft: `4px solid ${getInquiryColor(contact.inquiry_type)}`
                            }}>
                                <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                                    {contact.subject}
                                </div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
                                    {contact.message}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
